import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import API, { getPayrollTrend } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';
import { Download, Users, TrendingUp, FileText, AlertTriangle, BarChart2 } from 'lucide-react';

const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const Reports = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year,  setYear]  = useState(new Date().getFullYear());

  const { data: dashboard } = useQuery({
    queryKey: ['report-dashboard'],
    queryFn : () => API.get('/reports/dashboard').then(r => r.data.data)
  });

  const { data: headcount } = useQuery({
    queryKey: ['report-headcount'],
    queryFn : () => API.get('/reports/headcount').then(r => r.data.data),
    enabled : activeTab === 'headcount'
  });

  const { data: attrition } = useQuery({
    queryKey: ['report-attrition'],
    queryFn : () => API.get('/reports/attrition').then(r => r.data.data),
    enabled : activeTab === 'attrition'
  });

  const { data: payrollReport } = useQuery({
    queryKey: ['report-payroll', month, year],
    queryFn : () => API.get(`/reports/payroll?month=${month}&year=${year}`).then(r => r.data.data),
    enabled : activeTab === 'payroll'
  });

  const { data: compliance } = useQuery({
    queryKey: ['report-compliance', month, year],
    queryFn : () => API.get(`/reports/compliance?month=${month}&year=${year}`).then(r => r.data.data),
    enabled : activeTab === 'compliance'
  });

  const { data: pipeline } = useQuery({
    queryKey: ['report-pipeline'],
    queryFn : () => API.get('/reports/pipeline').then(r => r.data.data),
    enabled : activeTab === 'pipeline'
  });

  const { data: payrollTrend } = useQuery({
    queryKey: ['report-payroll-trend'],
    queryFn : () => getPayrollTrend({ months_back: 6 }).then(r => r.data.data),
    enabled : activeTab === 'payroll-analytics'
  });

  const tabs = [
    { id: 'dashboard',        label: 'Overview',         icon: TrendingUp  },
    { id: 'headcount',        label: 'Headcount',        icon: Users       },
    { id: 'attrition',        label: 'Attrition',        icon: TrendingUp  },
    { id: 'payroll',          label: 'Payroll',          icon: FileText    },
    { id: 'payroll-analytics',label: 'Payroll Analytics',icon: BarChart2   },
    { id: 'pipeline',         label: 'Pipeline',         icon: Users       },
    { id: 'compliance',       label: 'Compliance',       icon: AlertTriangle }
  ];

  const downloadCSV = (type) => {
    const token = localStorage.getItem('accessToken');
    const url   = type === 'candidates'
      ? 'http://localhost:5000/api/v1/reports/export/candidates'
      : 'http://localhost:5000/api/v1/reports/export/deployed';
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${type}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
      });
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Reports & MIS</h1>
          <p className="text-sm text-gray-400 mt-0.5">Business intelligence and analytics</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => downloadCSV('candidates')}
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            <Download size={14} />
            Candidates CSV
          </button>
          <button onClick={() => downloadCSV('deployed')}
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            <Download size={14} />
            Deployed CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition
              ${activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Month/Year Filter */}
      {['payroll', 'compliance'].includes(activeTab) && (
        <div className="flex gap-3">
          <select value={month} onChange={e => setMonth(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
            {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
            <option value={2025}>2025</option>
            <option value={2026}>2026</option>
          </select>
        </div>
      )}

      {/* ── OVERVIEW TAB ── */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Candidates',    value: dashboard.workforce?.total_candidates },
              { label: 'Active Deployments',  value: dashboard.workforce?.active_deployments },
              { label: 'On Bench',            value: dashboard.workforce?.on_bench },
              { label: 'Open Positions',      value: dashboard.recruitment?.open_positions }
            ].map(item => (
              <div key={item.label} className="bg-white rounded-xl border border-gray-100 p-4">
                <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                <p className="text-2xl font-semibold text-gray-900">{item.value ?? 0}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Revenue This Month', value: `₹${Number(dashboard.finance?.this_month_revenue || 0).toLocaleString('en-IN')}` },
              { label: 'Collected',          value: `₹${Number(dashboard.finance?.this_month_collected || 0).toLocaleString('en-IN')}` },
              { label: 'Payroll Cost',       value: `₹${Number(dashboard.finance?.this_month_payroll || 0).toLocaleString('en-IN')}` },
              { label: 'Overdue Invoices',   value: dashboard.alerts?.overdue_invoices }
            ].map(item => (
              <div key={item.label} className="bg-white rounded-xl border border-gray-100 p-4">
                <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                <p className="text-2xl font-semibold text-gray-900">{item.value ?? 0}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── HEADCOUNT TAB ── */}
      {activeTab === 'headcount' && headcount && (
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Candidates by Status</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={headcount.by_status?.map(s => ({ name: s.status, count: s._count }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Monthly Headcount Trend</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={headcount.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── ATTRITION TAB ── */}
      {activeTab === 'attrition' && attrition && (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total Terminated', value: attrition.total_terminated },
              { label: 'Attrition Rate',   value: `${attrition.attrition_rate_pct}%` },
              { label: 'Avg Tenure',       value: `${attrition.avg_tenure_days} days` }
            ].map(item => (
              <div key={item.label} className="bg-white rounded-xl border border-gray-100 p-4">
                <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                <p className="text-2xl font-semibold text-gray-900">{item.value ?? 0}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Monthly Attrition</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={attrition.monthly_attrition}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="terminated" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── PAYROLL TAB ── */}
      {activeTab === 'payroll' && payrollReport && (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Gross Salary',     value: `₹${Number(payrollReport.totals?.gross_salary || 0).toLocaleString('en-IN')}` },
              { label: 'Net Salary',       value: `₹${Number(payrollReport.totals?.net_salary || 0).toLocaleString('en-IN')}` },
              { label: 'Total Employer Cost', value: `₹${Number(payrollReport.totals?.total_employer_cost || 0).toLocaleString('en-IN')}` }
            ].map(item => (
              <div key={item.label} className="bg-white rounded-xl border border-gray-100 p-4">
                <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                <p className="text-2xl font-semibold text-gray-900">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
              <p className="text-xs font-medium text-gray-500 uppercase">Payroll Details — {months[month - 1]} {year}</p>
              <button
                onClick={() => {
                  const token = localStorage.getItem('accessToken');
                  fetch(`http://localhost:5000/api/v1/reports/payroll?month=${month}&year=${year}&format=csv`, {
                    headers: { Authorization: `Bearer ${token}` }
                  }).then(r => r.blob()).then(blob => {
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `payroll-${month}-${year}.csv`;
                    a.click();
                  });
                }}
                className="flex items-center gap-1 text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
                <Download size={12} />
                Download CSV
              </button>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Employee</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Gross</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">PF</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">ESI</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Net</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {payrollReport.records?.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 text-sm text-gray-900">
                      {p.candidates?.first_name} {p.candidates?.last_name}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600">₹{Number(p.gross_salary).toLocaleString('en-IN')}</td>
                    <td className="px-5 py-3 text-sm text-gray-600">₹{Number(p.pf_employee).toLocaleString('en-IN')}</td>
                    <td className="px-5 py-3 text-sm text-gray-600">₹{Number(p.esic_employee).toLocaleString('en-IN')}</td>
                    <td className="px-5 py-3 text-sm font-medium text-green-600">₹{Number(p.net_salary).toLocaleString('en-IN')}</td>
                    <td className="px-5 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{p.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── PAYROLL ANALYTICS TAB (Step 19) ── */}
      {activeTab === 'payroll-analytics' && (
        <div className="space-y-5">
          {!payrollTrend ? (
            <div className="text-center py-12 text-gray-400">Loading payroll analytics...</div>
          ) : (
            <>
              {/* Trend chart */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">6-Month Salary Trend (₹)</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={payrollTrend.trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={v => `₹${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
                    <Legend />
                    <Line type="monotone" dataKey="gross" name="Gross Salary" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="net"   name="Net Salary"   stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-2 gap-5">
                {/* Statutory burden trend */}
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Statutory Burden Trend (₹)</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={payrollTrend.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={v => `₹${(v/1000).toFixed(0)}K`} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
                      <Legend />
                      <Bar dataKey="pf_total"  name="PF (Both)"   fill="#ef4444" radius={[2,2,0,0]} />
                      <Bar dataKey="esi_total" name="ESI (Both)"  fill="#f97316" radius={[2,2,0,0]} />
                      <Bar dataKey="pt"        name="Prof. Tax"   fill="#8b5cf6" radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Headcount trend */}
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Headcount on Payroll</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={payrollTrend.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="head_count" name="Employees" fill="#3b82f6" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Client-wise breakdown */}
              {payrollTrend.by_client?.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Client-wise Payroll (Current Month)</h3>
                  <div className="grid grid-cols-2 gap-5">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={payrollTrend.by_client} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tickFormatter={v => `₹${(v/1000).toFixed(0)}K`} tick={{ fontSize: 10 }} />
                        <YAxis type="category" dataKey="client_name" width={100} tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
                        <Bar dataKey="gross_salary" name="Gross" fill="#3b82f6" radius={[0,4,4,0]} />
                      </BarChart>
                    </ResponsiveContainer>

                    <div className="overflow-auto max-h-56">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left text-xs text-gray-400 pb-2">Client</th>
                            <th className="text-right text-xs text-gray-400 pb-2">Count</th>
                            <th className="text-right text-xs text-gray-400 pb-2">Gross</th>
                            <th className="text-right text-xs text-gray-400 pb-2">Net</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {payrollTrend.by_client.map((c, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="py-2 text-xs text-gray-700">{c.client_name}</td>
                              <td className="py-2 text-right text-xs text-gray-600">{c.head_count}</td>
                              <td className="py-2 text-right text-xs font-medium text-gray-900">
                                ₹{Number(c.gross_salary).toLocaleString('en-IN')}
                              </td>
                              <td className="py-2 text-right text-xs font-medium text-green-700">
                                ₹{Number(c.net_salary).toLocaleString('en-IN')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── PIPELINE TAB ── */}
      {activeTab === 'pipeline' && pipeline && (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total Applications', value: pipeline.conversion?.total },
              { label: 'Selected',           value: pipeline.conversion?.selected },
              { label: 'Conversion Rate',    value: `${pipeline.conversion?.rate}%` },
            ].map(item => (
              <div key={item.label} className="bg-white rounded-xl border border-gray-100 p-4">
                <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                <p className="text-2xl font-semibold text-gray-900">{item.value ?? 0}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Applications by Stage</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={pipeline.applications_by_stage?.map(s => ({ name: s.stage, count: s._count }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── COMPLIANCE TAB ── */}
      {activeTab === 'compliance' && compliance && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Missing Aadhar/PAN',   value: compliance.document_compliance?.missing_aadhar_or_pan, color: 'text-red-600' },
              { label: 'Pending Timesheets',   value: compliance.payroll_compliance?.pending_timesheets,     color: 'text-amber-600' },
              { label: 'Unpaid Payrolls',      value: compliance.payroll_compliance?.unpaid_payrolls,        color: 'text-amber-600' }
            ].map(item => (
              <div key={item.label} className="bg-white rounded-xl border border-gray-100 p-4">
                <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                <p className={`text-2xl font-semibold ${item.color}`}>{item.value ?? 0}</p>
              </div>
            ))}
          </div>

          {compliance.expiring_contracts?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-amber-50">
                <p className="text-xs font-medium text-amber-700 uppercase">Contracts Expiring in 30 Days</p>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Candidate</th>
                    <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Client</th>
                    <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">End Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {compliance.expiring_contracts.map(dep => (
                    <tr key={dep.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 text-sm text-gray-900">
                        {dep.candidates?.first_name} {dep.candidates?.last_name}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600">{dep.clients?.company_name}</td>
                      <td className="px-5 py-3 text-sm text-gray-600">{dep.end_date?.slice(0, 10) || 'No end date'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;